import AuthLayout from '@/components/auth/AuthLayout';
import FloatingLabelInput from '@/components/FloatingLabelInput';
import GoogleSignInButton from '@/components/GoogleSignInButton';
import SubmitButton from '@/components/SubmitButton';
import useAutoFocus from '@/hooks/useAutoFocus';
import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import { Checkbox } from 'antd';
import axios from 'axios';
import { signIn } from 'next-auth/react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import styles from './SignIn.module.scss';

const SignUp = () => {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
  });

  // Auto-focus name field on mount
  useAutoFocus('name', isLoading);

  // Handle OAuth errors
  useEffect(() => {
    const { error } = router.query;

    if (error) {
      let errorMessage = '';
      let errorTitle = 'Sign Up Error';

      switch (error) {
        case 'OAuthAccountNotLinked':
          errorTitle = 'Account Already Exists';
          errorMessage = (
            <div>
              An account with this email already exists.{' '}
              <Link href="/auth/signin" style={{ color: '#fff', textDecoration: 'underline' }}>
                Sign in here
              </Link>{' '}
              or use a different email address.
            </div>
          );
          break;
        case 'OAuthSignin':
        case 'OAuthCallback':
          errorMessage =
            'There was a problem connecting to Google. Please try again or sign up with your email address.';
          break;
        case 'OAuthCreateAccount':
          errorMessage =
            'Unable to create your account with Google. Please try signing up with your email address instead.';
          break;
        default:
          errorMessage = 'An error occurred during sign up. Please try again.';
      }

      toast.error(
        <div>
          <strong>{errorTitle}</strong>
          <div style={{ marginTop: '4px', fontSize: '14px' }}>{errorMessage}</div>
        </div>,
        {
          duration: 8000,
          position: 'top-center',
          style: {
            maxWidth: '500px',
          },
        }
      );

      // Clean up URL after showing error
      router.replace('/auth/signup', undefined, { shallow: true });
    }
  }, [router.query.error]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!agreedToTerms) {
      toast.error('Please accept the Terms & Conditions and Privacy Policy');
      return;
    }

    setIsLoading(true);

    try {
      const data = {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        agreedToTerms: true,
      };

      await axios.post('/api/auth/signup', data);

      const result = await signIn('credentials', {
        email: data.email,
        password: data.password,
        redirect: false,
      });

      if (result.error) {
        throw new Error(result.error);
      }

      window.location.href = '/account/dashboard';
    } catch (error) {
      toast.error(error.response?.data?.error || 'Unable to create account');
    } finally {
      setIsLoading(false);
    }
  };

  const isFormValid = formData.name && formData.email && formData.password && agreedToTerms;

  // Brand rail content
  const brandContent = {
    headline: "Unleash Your Brand's Full Trust Potential.",
    subheadline: 'Get a verified logo, secure messages, and AI-powered insights.',
    features: [
      'Guarantee your brand logo is recognized and trusted everywhere.',
      'Stop email fraud and elevate your sender reputation with AI.',
      'Gain effortless control over all your domain security protocols.',
      'Automate renewals and compliance, so your brand is always protected.',
    ],
  };

  return (
    <AuthLayout
      headline={brandContent.headline}
      subheadline={brandContent.subheadline}
      features={brandContent.features}
      variant="2"
    >
      <div className={styles.authFormContainer}>
        <div className={styles.formHeader}>
          <h1 className={styles.formTitle}>Sign Up</h1>
          <p className={styles.formSubtitle}>Create Your Verified Brand Identity, it's free.</p>
        </div>

        <div className={styles.formBody}>
          {/* Google Sign In */}
          <GoogleSignInButton disabled={isLoading} mode="signup" />

          <div className={styles.authDivider}>
            <span>or</span>
          </div>

          {/* Sign Up Form */}
          <form onSubmit={handleSubmit}>
            <FloatingLabelInput
              id="name"
              name="name"
              type="text"
              label="Full Name"
              value={formData.name}
              onChange={handleChange}
              required
              disabled={isLoading}
              autoComplete="name"
            />

            <FloatingLabelInput
              id="email"
              name="email"
              type="email"
              label="Email Address"
              value={formData.email}
              onChange={handleChange}
              required
              disabled={isLoading}
              autoComplete="email"
            />

            <div style={{ position: 'relative', marginBottom: '24px' }}>
              <FloatingLabelInput
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                label="Password"
                value={formData.password}
                onChange={handleChange}
                required
                minLength={8}
                disabled={isLoading}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                disabled={isLoading}
                style={{
                  position: 'absolute',
                  right: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  color: isLoading ? '#ccc' : '#666',
                  fontSize: '18px',
                  zIndex: 10,
                }}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                tabIndex={-1}
              >
                {showPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
              </button>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <Checkbox
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                disabled={isLoading}
              >
                I agree to the{' '}
                <Link href="/terms" target="_blank" rel="noopener noreferrer nofollow">
                  Terms & Conditions
                </Link>
              </Checkbox>
            </div>

            <SubmitButton isLoading={isLoading} disabled={!isFormValid} className="width-100">
              Create Account
              <span className="line-rounded-icon link-icon-right"></span>
            </SubmitButton>
          </form>

          {/* Footer Links */}
          <div className={styles.formFooter}>
            <div className={styles.footerLink}>
              Already have an account? <Link href="/auth/signin">Sign In</Link>
            </div>
            <div className={styles.footerLink}>
              Are you a reseller? <Link href="/auth/partner-signup">Sign up as a Partner</Link>
            </div>
          </div>
        </div>
      </div>
    </AuthLayout>
  );
};

export default SignUp;
